import { escapeHTML } from '../../utils/escape.js';
import { ANTICIPACION_HORAS } from '../../services/reservation.rules.js';
import { CONFIG } from '../../config.js';

/** Número del local, del único lugar donde vive (ver CONFIG.WHATSAPP). */
const WHATSAPP_URL = `https://wa.me/${CONFIG.WHATSAPP}`;

/**
 * Componentes para el Wizard de Reservas.
 * Cada paso es una función pura que devuelve HTML.
 *
 * ARIA conventions used throughout:
 *   - Each step is a `role="region"` with aria-labelledby pointing to its <h2>.
 *   - Option groups (momentos, fechas, áreas, slots) use role="radiogroup" + role="radio" + aria-checked.
 *   - Disabled interactive elements use aria-disabled="true" (announced by screen readers,
 *     unlike the HTML `disabled` attribute which silences them).
 *   - Decorative icons inside buttons carry aria-hidden="true"; the button itself has aria-label.
 *   - The wizard container in index.html carries aria-live="polite" for step-change announcements.
 */

export const Step1 = (reserva) => `
    <div role="region" aria-labelledby="step1-title">
        <h2 id="step1-title" class="text-3xl font-black text-text-primary uppercase tracking-tighter italic mb-4 text-center">¿Quién nos visita?</h2>
        <div class="bg-base rounded-[2rem] border border-border-subtle shadow-xl p-6 space-y-4">
            <div class="space-y-2.5">
                <div class="text-left">
                    <label for="inputNombre" class="text-[10px] font-black text-text-muted uppercase tracking-widest ml-4 mb-1.5 block">Nombre</label>
                    <input type="text" id="inputNombre" name="nombre" value="${escapeHTML(reserva.nombre)}" placeholder="Tu nombre"
                        autocomplete="given-name"
                        class="w-full bg-surface border-none rounded-2xl px-6 py-2.5 text-sm font-bold focus:ring-2 focus:ring-brand">
                </div>
                <div class="text-left">
                    <label for="inputApellido" class="text-[10px] font-black text-text-muted uppercase tracking-widest ml-4 mb-1.5 block">Apellido</label>
                    <input type="text" id="inputApellido" name="apellido" value="${escapeHTML(reserva.apellido)}" placeholder="Tu apellido"
                        autocomplete="family-name"
                        class="w-full bg-surface border-none rounded-2xl px-6 py-2.5 text-sm font-bold focus:ring-2 focus:ring-brand">
                </div>
                <div class="text-left">
                    <label for="inputTelCodigo" class="text-[10px] font-black text-text-muted uppercase tracking-widest ml-4 mb-1.5 block">Código de área</label>
                    <!-- El "0" del código va como prefijo FIJO fuera del campo:
                         el usuario tipea solo el código (ej. 3572), menos confusión. -->
                    <div class="flex items-stretch bg-surface rounded-2xl focus-within:ring-2 focus-within:ring-brand">
                        <span class="flex items-center pl-6 pr-1 text-sm font-black text-text-muted select-none" aria-hidden="true">0</span>
                        <input type="tel" id="inputTelCodigo" name="telCodigo" value="${escapeHTML(reserva.telCodigo)}" placeholder="3572"
                            autocomplete="tel-area-code" inputmode="numeric"
                            class="flex-1 min-w-0 bg-transparent border-none rounded-r-2xl pl-1 pr-6 py-2.5 text-sm font-bold focus:outline-none focus:ring-0">
                    </div>
                </div>
                <div class="text-left">
                    <label for="inputTelNumero" class="text-[10px] font-black text-text-muted uppercase tracking-widest ml-4 mb-1.5 block">Celular</label>
                    <!-- El "15" va como prefijo FIJO fuera del campo: el usuario
                         tipea solo el número (ej. 123456). -->
                    <div class="flex items-stretch bg-surface rounded-2xl focus-within:ring-2 focus-within:ring-brand">
                        <span class="flex items-center pl-6 pr-1 text-sm font-black text-text-muted select-none" aria-hidden="true">15</span>
                        <input type="tel" id="inputTelNumero" name="telNumero" value="${escapeHTML(reserva.telNumero)}" placeholder="123456"
                            autocomplete="tel-local" inputmode="numeric"
                            class="flex-1 min-w-0 bg-transparent border-none rounded-r-2xl pl-1 pr-6 py-2.5 text-sm font-bold focus:outline-none focus:ring-0">
                    </div>
                </div>
            </div>
            <div>
                <label id="personas-label" class="text-[10px] font-black text-text-muted uppercase tracking-widest block mb-2 text-center">Cantidad de personas</label>
                <div class="flex items-center justify-between bg-surface rounded-2xl p-2 border border-border-subtle max-w-[220px] mx-auto"
                     role="group" aria-labelledby="personas-label">
                    <button data-action="adj-personas" data-val="-1"
                        aria-label="Reducir personas"
                        class="w-10 h-10 rounded-xl bg-base shadow-sm flex items-center justify-center text-text-muted">
                        <i class="fa-solid fa-minus" aria-hidden="true"></i>
                    </button>
                    <span aria-live="polite" aria-atomic="true" class="text-2xl font-black text-text-primary">${escapeHTML(String(reserva.personas))}</span>
                    <button data-action="adj-personas" data-val="1"
                        aria-label="Aumentar personas"
                        class="w-10 h-10 rounded-xl bg-base shadow-sm flex items-center justify-center text-text-muted">
                        <i class="fa-solid fa-plus" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
            <button data-action="reserva-next" class="w-full py-3.5 bg-brand text-white rounded-2xl font-black uppercase text-xs shadow-lg">Siguiente</button>
        </div>
    </div>
`;

export const Step2 = (reserva, dates) => `
    <div role="region" aria-labelledby="step2-title">
        <h2 id="step2-title" class="text-3xl font-black text-text-primary uppercase tracking-tighter italic mb-4 text-center">Elegí el día</h2>
        ${dates.length > 0
            ? `<div role="radiogroup" aria-label="Elegí el día" class="grid grid-cols-4 gap-3 mb-4">
                ${dates.map(d => {
                    const [dayNum, , monthAbbr] = d.label.split(' ');
                    const isActive = reserva.fecha === d.iso;
                    return `
                        <button data-action="set-fecha" data-value="${escapeHTML(d.iso)}"
                            role="radio" aria-checked="${isActive}"
                            class="flex flex-col items-center justify-center gap-1 py-4 rounded-3xl border-2 transition-all ${isActive ? 'bg-brand border-brand text-white shadow-lg' : 'bg-base border-border-subtle text-text-primary'}">
                            <span class="text-[8px] font-black uppercase tracking-widest ${isActive ? 'text-white/60' : 'text-text-muted'}">${escapeHTML(d.dayName)}</span>
                            <span class="text-3xl font-black leading-none">${escapeHTML(dayNum)}</span>
                            <span class="text-[8px] font-black uppercase tracking-widest ${isActive ? 'text-white/60' : 'text-text-muted'}">${escapeHTML(monthAbbr)}</span>
                        </button>
                    `;
                }).join('')}
            </div>`
            : `<div class="py-10 px-8 bg-amber-50 rounded-[2.5rem] border border-amber-100 text-amber-900 text-center font-bold text-xs italic mb-4">
                No hay horarios disponibles próximamente.
            </div>`
        }
        <!-- Las reservas exigen ${ANTICIPACION_HORAS} h de anticipación (los turnos
             más próximos se filtran en el paso siguiente). Para algo de último
             momento, se deriva al WhatsApp directo. -->
        <a href="${WHATSAPP_URL}" target="_blank" rel="noopener noreferrer"
            class="flex items-center justify-center gap-2 text-center text-[11px] font-bold text-text-muted bg-surface border border-border-subtle rounded-2xl px-4 py-3 mb-2 hover:border-brand transition-colors">
            <i class="fa-brands fa-whatsapp text-brand text-base" aria-hidden="true"></i>
            <span>¿Para hoy con menos de ${ANTICIPACION_HORAS} h de anticipación? Escribinos por WhatsApp</span>
        </a>
        <button data-action="reserva-back" class="w-full text-[10px] font-black text-text-muted uppercase py-3 text-center">Volver</button>
    </div>
`;

export const StepLoading = () => `
    <div class="flex flex-col items-center justify-center py-20">
        <div class="w-24 h-24 rounded-full bg-surface border border-border-subtle flex items-center justify-center mb-8 shadow-sm">
            <i id="reservaLoadingIcon" class="fa-solid fa-utensils text-3xl text-brand" aria-hidden="true"></i>
        </div>
        <p class="text-xs font-black text-text-muted uppercase tracking-widest text-center leading-relaxed">
            Estamos consultando<br>disponibilidad...
        </p>
    </div>
`;

export const Step3 = (reserva, momentos, slotsHtml, areasHtml) => `
    <div role="region" aria-labelledby="step3-title">
        <h2 id="step3-title" class="text-3xl font-black text-text-primary uppercase tracking-tighter italic mb-4 text-center">Reserva tu mesa</h2>
        <div class="space-y-2 mb-4" role="radiogroup" aria-label="Elegí el momento">
            ${momentos.map(m => {
                const isActive = reserva.momento === m;
                return isActive ? `
                    <div class="rounded-3xl overflow-hidden shadow-md">
                        <button data-action="set-momento" data-value="${escapeHTML(m)}"
                            role="radio" aria-checked="true" aria-expanded="true"
                            class="w-full px-6 py-3.5 flex items-center justify-between bg-brand text-white">
                            <span class="text-sm font-black uppercase tracking-wider">${escapeHTML(m)}</span>
                            <i class="fa-solid fa-chevron-up text-white/50" aria-hidden="true"></i>
                        </button>
                        <div class="p-4 bg-base">
                            <div role="radiogroup" aria-label="Elegí el horario" class="grid grid-cols-3 gap-2">
                                ${slotsHtml}
                            </div>
                            ${areasHtml}
                        </div>
                    </div>
                ` : `
                    <button data-action="set-momento" data-value="${escapeHTML(m)}"
                        role="radio" aria-checked="false" aria-expanded="false"
                        class="w-full px-6 py-3.5 rounded-3xl bg-surface border-2 border-transparent flex items-center justify-between text-text-secondary transition-all active:scale-95">
                        <span class="text-sm font-black uppercase tracking-wider">${escapeHTML(m)}</span>
                        <i class="fa-solid fa-chevron-right text-text-muted text-xs" aria-hidden="true"></i>
                    </button>
                `;
            }).join('')}
        </div>
        <div class="flex flex-col gap-3">
            <button data-action="reserva-next"
                ${!reserva.area ? 'aria-disabled="true" disabled' : ''}
                class="w-full py-3.5 bg-brand text-white rounded-2xl font-black uppercase text-xs shadow-lg disabled:opacity-30">
                Continuar
            </button>
            <button data-action="reserva-back" class="text-[10px] font-black text-text-muted uppercase py-2.5">Volver</button>
        </div>
    </div>
`;

export const Step4 = (reserva) => `
    <div role="region" aria-labelledby="step4-title">
        <h2 id="step4-title" class="text-3xl font-black text-text-primary uppercase tracking-tighter italic mb-8 text-center">Confirmar Datos</h2>
        <div class="bg-invert-bg rounded-[3rem] p-10 text-left border border-white/5 shadow-2xl space-y-8">
            <div class="space-y-1">
                <span class="text-[9px] font-black text-white/30 uppercase tracking-widest">Titular</span>
                <p class="text-2xl font-black text-invert-text italic">${escapeHTML(reserva.nombre)} ${escapeHTML(reserva.apellido)}</p>
            </div>
            <div class="grid grid-cols-2 gap-6 pt-4 border-t border-white/5">
                <div><span class="text-[9px] font-black text-white/30 uppercase tracking-widest">Fecha</span><p class="text-sm font-bold text-invert-text">${escapeHTML(reserva.fecha)}</p></div>
                <div><span class="text-[9px] font-black text-white/30 uppercase tracking-widest">Turno</span><p class="text-sm font-bold text-invert-text">${escapeHTML(reserva.hora)} — ${escapeHTML(reserva.momento)}</p></div>
                <div><span class="text-[9px] font-black text-white/30 uppercase tracking-widest">Comensales</span><p class="text-sm font-bold text-invert-text">${escapeHTML(String(reserva.personas))} pers.</p></div>
                <div><span class="text-[9px] font-black text-white/30 uppercase tracking-widest">Sector</span><p class="text-sm font-bold text-invert-text">${escapeHTML(reserva.area)}</p></div>
            </div>
            <div class="flex flex-col gap-3 pt-6">
                <button data-action="reserva-finish" id="btnFinalizar" class="w-full py-5 bg-base text-text-primary rounded-2xl font-black uppercase text-xs shadow-xl">Confirmar Reserva</button>
                <button data-action="reserva-back" class="w-full py-4 text-[10px] font-black text-white/20 uppercase text-center">Volver</button>
            </div>
        </div>
    </div>
`;

export const Step5 = (reserva) => `
    <div role="region" aria-labelledby="step5-title">
        <div class="text-center flex flex-col items-center">
            <div class="w-24 h-24 rounded-full bg-brand flex items-center justify-center mb-8 shadow-lg" aria-hidden="true">
                <i class="fa-solid fa-check text-4xl text-white" aria-hidden="true"></i>
            </div>
            <h2 id="step5-title" class="text-3xl font-black text-text-primary uppercase tracking-tighter mb-3 italic">Reserva confirmada</h2>
            <p class="text-text-muted text-sm font-medium mb-10">¡Te esperamos, ${escapeHTML(reserva.nombre)}!</p>
            <div class="w-full bg-surface rounded-[2rem] p-8 text-left space-y-4 mb-10">
                <div class="flex justify-between items-center border-b border-border-subtle pb-4">
                    <span class="text-[10px] font-black text-text-muted uppercase tracking-widest">Fecha</span>
                    <span class="text-sm font-black text-text-primary">${escapeHTML(reserva.fecha)}</span>
                </div>
                <div class="flex justify-between items-center border-b border-border-subtle pb-4">
                    <span class="text-[10px] font-black text-text-muted uppercase tracking-widest">Turno</span>
                    <span class="text-sm font-black text-text-primary">${escapeHTML(reserva.hora)} — ${escapeHTML(reserva.momento)}</span>
                </div>
                <div class="flex justify-between items-center border-b border-border-subtle pb-4">
                    <span class="text-[10px] font-black text-text-muted uppercase tracking-widest">Comensales</span>
                    <span class="text-sm font-black text-text-primary">${escapeHTML(String(reserva.personas))} personas</span>
                </div>
                <div class="flex justify-between items-center">
                    <span class="text-[10px] font-black text-text-muted uppercase tracking-widest">Sector</span>
                    <span class="text-sm font-black text-text-primary">${escapeHTML(reserva.area)}</span>
                </div>
            </div>
            <button data-action="reserva-reset" class="text-[10px] font-black text-text-muted uppercase py-4">Volver al inicio</button>
        </div>
    </div>
`;
